import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface DateRangePickerProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  label?: string;
}

export default function DateRangePicker({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  label = 'Date Range',
}: DateRangePickerProps) {
  return (
    <div className="space-y-2">
      {label && (
        <Label>{label}</Label>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => onFromDateChange(e.target.value)}
        />
        <span className="text-muted-foreground text-sm">to</span>
        <Input
          type="date"
          value={toDate}
          onChange={(e) => onToDateChange(e.target.value)}
        />
      </div>
    </div>
  );
}

// Quick date range presets
interface DateRangePreset {
  label: string;
  getRange: () => { fromDate: string; toDate: string };
}

export function useDateRangePresets() {
  const today = new Date();

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const presets: DateRangePreset[] = [
    {
      label: 'Next 7 days',
      getRange: () => {
        const end = new Date(today);
        end.setDate(end.getDate() + 7);
        return { fromDate: formatDate(today), toDate: formatDate(end) };
      },
    },
    {
      label: 'Next 30 days',
      getRange: () => {
        const end = new Date(today);
        end.setDate(end.getDate() + 30);
        return { fromDate: formatDate(today), toDate: formatDate(end) };
      },
    },
    {
      label: 'Next 60 days',
      getRange: () => {
        const end = new Date(today);
        end.setDate(end.getDate() + 60);
        return { fromDate: formatDate(today), toDate: formatDate(end) };
      },
    },
    {
      label: 'Next 90 days',
      getRange: () => {
        const end = new Date(today);
        end.setDate(end.getDate() + 90);
        return { fromDate: formatDate(today), toDate: formatDate(end) };
      },
    },
    {
      label: 'This month',
      getRange: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { fromDate: formatDate(start), toDate: formatDate(end) };
      },
    },
    {
      label: 'Next month',
      getRange: () => {
        const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        return { fromDate: formatDate(start), toDate: formatDate(end) };
      },
    },
  ];

  return presets;
}
